import React, { useContext, useState } from "react";
import { UserContext } from '../UserContext';
import { GeneralData, ChangeMail, ChangePassword, Important } from '../components/Account';
import { BuyStars, Manage, SubscriptionInfo } from '../components/Payments';
import Contactus from '../components/Contactus';
import NotificationSettings from '../components/NotificationSettings';
import { useTranslation } from "../LanguageContext";
import api from '../api';
import { publicProfileUrl } from '../utils/siteUrl';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function SubscriptionsSection() {
    const { user, setUser } = useContext(UserContext);
    const { t } = useTranslation();
    const subs = user.infs || [];
    const [loadingId, setLoadingId] = useState(null);
    const [setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleAction = async (sourceId, action) => {
        setLoadingId(sourceId);
        try {
            const res = await api.post(`/api/manage-subscription/`, { action, sub_id: sourceId });
            if (res.status === 200) {
                // Update local state with server response
                const updatedInfs = user.infs.map(inf => {
                    if (inf.subscription.source_id === sourceId) {
                        return {
                            ...inf,
                            subscription: {
                                ...inf.subscription,
                                cancel_at_period_end: res.data.cancel_at_period_end,
                            }
                        };
                    }
                    return inf;
                });
                setUser(prev => ({ ...prev, infs: updatedInfs }));
                showToast(res.data.message);
            }
        } catch (error) {
            const msg = error.response?.data?.error || 'An error occurred.';
            showToast(msg, 'error');
        } finally {
            setLoadingId(null);
        }
    };

    // Determine button props based on subscription state
    const getManageButton = (sub) => {
        const { subscription } = sub;
        const isLoading = loadingId === subscription.source_id;

        // Subscription has ended — no action possible
        if (subscription.ended_at) {
            return (
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg flex-shrink-0">
                    {t('settings.subsExpired')}
                </span>
            );
        }

        // Active but pending cancellation — show Reactivate
        if (subscription.cancel_at_period_end && subscription.status === 'active') {
            return (
                <button
                    type="button"
                    onClick={() => handleAction(sub.subscription.source_id, 'reactivate')}
                    disabled={isLoading}
                    className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg transition flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    {isLoading ? (
                        <div className="w-3 h-3 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    )}
                    {t('settings.subsReactivate')}
                </button>
            );
        }

        // Fully active — show Cancel
        if (subscription.status === 'active') {
            return (
                <button
                    type="button"
                    onClick={() => handleAction(sub.subscription.source_id, 'cancel')}
                    disabled={isLoading}
                    className="text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-lg transition flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    {isLoading ? (
                        <div className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    )}
                    {t('settings.subsCancel')}
                </button>
            );
        }

        // Inactive / other states
        return (
            <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg flex-shrink-0">
                {t('settings.subsStatusInactive')}
            </span>
        );
    };

    if (subs.length === 0) {
        return
    }
    else {
        return (
            <section className="rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#f9f9fc] relative">
                {/* Toast notification */}
                {/* {toast && (
                <div className={`absolute top-3 right-3 z-10 px-4 py-2.5 rounded-xl shadow-lg text-xs font-semibold flex items-center gap-2 animate-fade-in ${
                    toast.type === 'error'
                        ? 'bg-red-600 text-white'
                        : 'bg-emerald-600 text-white'
                }`}>
                    {toast.type === 'error' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                    {toast.message}
                </div>
            )} */}

                <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {t('settings.subsTitle')}
                    <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium ml-auto">{subs.length} {t('settings.subsTotal')}</span>
                </h2>

                {subs.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">{t('settings.subsEmpty')}</p>
                ) : (
                    <div className="space-y-3">
                        {subs.map((sub) => (
                            <div key={sub.id} className={`flex items-center gap-3 p-3 bg-white rounded-xl border transition ${sub.subscription.cancel_at_period_end && !sub.subscription.ended_at
                                ? 'border-amber-200 bg-amber-50/30'
                                : sub.subscription.ended_at
                                    ? 'border-slate-200 opacity-60'
                                    : 'border-slate-100 hover:border-slate-200'
                                }`}>
                                <img
                                    src={sub.inf_pp}
                                    alt={sub.username}
                                    className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <a href={publicProfileUrl(sub.username)} target="_blank" rel="noopener noreferrer">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{sub.username}</p>
                                    </a>
                                    <p className="text-xs text-slate-400">
                                        {t('settings.subsStarted')} {formatDate(sub.subscription.started_at)}
                                        {sub.subscription.cancel_at_period_end && !sub.subscription.ended_at && (
                                            <> · <span className="text-amber-600 font-medium">{t('settings.subsCancelsAtEnd')}</span></>
                                        )}
                                        {sub.subscription.ended_at && (
                                            <> · <span className="text-red-500">{t('settings.subsEnded')} {formatDate(sub.subscription.ended_at)}</span></>
                                        )}
                                    </p>
                                </div>
                                {/* <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                                sub.subscription.ended_at
                                    ? 'text-slate-500 bg-slate-100'
                                    : sub.subscription.cancel_at_period_end
                                        ? 'text-amber-600 bg-amber-50'
                                        : sub.subscription.status === 'active'
                                            ? 'text-emerald-600 bg-emerald-50'
                                            : 'text-red-600 bg-red-50'
                            }`}>
                                {sub.subscription.ended_at
                                    ? t('settings.subsStatusEnded')
                                    : sub.subscription.cancel_at_period_end
                                        ? t('settings.subsStatusCanceling')
                                        : sub.subscription.status === 'active'
                                            ? t('settings.subsStatusActive')
                                            : t('settings.subsStatusInactive')}
                            </span> */}
                                {getManageButton(sub)}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    }
}

function Settings() {
    const { user } = useContext(UserContext);
    const { t } = useTranslation();


    return (
        <div className="min-h-screen bg-slate-50 pb-24">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-4 sm:pt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">

                {/* Section 1: Account Info (Name, Email, Password) — full width */}
                <section className="rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#fafafa]">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t('settings.tabAccount')}
                    </h2>
                    <div className="space-y-4">
                        <GeneralData />
                        <ChangeMail />
                        {!user.third_party && <ChangePassword />}
                    </div>
                </section>

                {/* Section 2: Notifications — the tab badge is always on; this
                    is the opt-in for OS-level desktop notifications. */}
                <section className="rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#fafafa]">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        {t('tab.enableTitle')}
                    </h2>
                    <NotificationSettings />
                </section>

                {/* Section 3: Contact Us — full width */}
                <section className="rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#fafaf8]">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('settings.tabContact')}
                    </h2>
                    <Contactus />
                </section>

                {/* Section 4: Delete Account & Logout — full width */}
                <section className="lg:col-span-2 rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#fdf9f9]">
                    <Important />
                </section>

            </div>
        </div>
    );
}
export default Settings;